import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, CreditCard, Key, Camera, Trash2, Copy, Check, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TABS = [
  { id: 'account', icon: User, translationKey: 'settings_account' },
  { id: 'subscription', icon: CreditCard, translationKey: 'settings_subscription' },
  { id: 'api', icon: Key, translationKey: 'settings_api' },
];

export default function SettingsPage() {
  const { user, token, updateProfile, changePassword, refreshUser, setUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);

  const initialTab = searchParams.get('tab') || 'account';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Account form
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [billing, setBilling] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [profileError, setProfileError] = useState('');

  // Password form
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMessage, setPwMessage] = useState('');
  const [pwError, setPwError] = useState('');

  // API key
  const [newApiKey, setNewApiKey] = useState('');
  const [apiCopied, setApiCopied] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    setName(user.name || '');
    setEmail(user.email || '');
    setBilling(user.billing_address || '');
  }, [user, navigate]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && TABS.some(t => t.id === tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  if (!user) return null;

  const handleSaveProfile = async () => {
    setSaving(true);
    setProfileError('');
    setSaved(false);
    try {
      await updateProfile({ name, email, billing_address: billing });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setProfileError(err.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPwError('');
    setPwMessage('');
    if (newPw !== confirmPw) {
      setPwError(t('settings_pw_mismatch'));
      return;
    }
    setPwSaving(true);
    try {
      await changePassword({ current_password: currentPw, new_password: newPw });
      setPwMessage(t('settings_pw_changed'));
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
    } catch (err) {
      setPwError(err.response?.data?.detail || 'Error');
    } finally {
      setPwSaving(false);
    }
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const res = await axios.put(`${API_URL}/api/auth/avatar`, { avatar_base64: reader.result }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(res.data);
      } catch (err) {
        // silently fail
      }
    };
    reader.readAsDataURL(file);
  };

  const handleAvatarDelete = async () => {
    try {
      const res = await axios.delete(`${API_URL}/api/auth/avatar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
    } catch (err) {
      // silently fail
    }
  };

  const handleGenerateApiKey = async () => {
    setApiLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/api-key`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewApiKey(res.data.api_key);
      await refreshUser();
    } catch (err) {
      // silently fail
    } finally {
      setApiLoading(false);
    }
  };

  const handleRevokeApiKey = async () => {
    setApiLoading(true);
    try {
      await axios.delete(`${API_URL}/api/auth/api-key`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewApiKey('');
      await refreshUser();
    } catch (err) {
      // silently fail
    } finally {
      setApiLoading(false);
    }
  };

  const copyApiKey = () => {
    navigator.clipboard.writeText(newApiKey);
    setApiCopied(true);
    setTimeout(() => setApiCopied(false), 2000);
  };

  const avatarLetter = user.name ? user.name[0].toUpperCase() : '?';

  const inputClass = "w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/25 transition-colors";
  const labelClass = "block text-sm font-medium text-slate-300 mb-2";
  const btnPrimary = "px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-blue-600 text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50";

  return (
    <div className="min-h-screen pt-24 pb-12 px-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-8">{t('settings_title')}</h1>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar */}
          <div className="md:w-56 flex-shrink-0">
            <div className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible">
              {TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                      : 'text-slate-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {t(tab.translationKey)}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2 }}
            >
              {/* ===== ACCOUNT TAB ===== */}
              {activeTab === 'account' && (
                <div className="space-y-8">
                  {/* Avatar section */}
                  <div className="glass rounded-2xl p-6 border border-white/5">
                    <h3 className="text-white font-medium mb-4">{t('settings_avatar')}</h3>
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-full overflow-hidden ring-2 ring-white/10">
                        {user.avatar_url ? (
                          <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center text-white font-bold text-2xl">
                            {avatarLetter}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
                        <button onClick={() => fileInputRef.current?.click()} className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-slate-300 hover:text-white hover:bg-white/10 transition-colors text-sm flex items-center gap-2">
                          <Camera className="w-4 h-4" /> {t('settings_upload_photo')}
                        </button>
                        {user.avatar_url && (
                          <button onClick={handleAvatarDelete} className="px-4 py-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-colors text-sm flex items-center gap-2">
                            <Trash2 className="w-4 h-4" /> {t('settings_remove_photo')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Profile form */}
                  <div className="glass rounded-2xl p-6 border border-white/5">
                    <div className="space-y-4">
                      <div>
                        <label className={labelClass}>{t('settings_name')}</label>
                        <input type="text" value={name} onChange={e => setName(e.target.value)} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>{t('settings_email')}</label>
                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>{t('settings_billing')}</label>
                        <textarea value={billing} onChange={e => setBilling(e.target.value)} rows={3} className={inputClass} />
                      </div>
                      {profileError && <p className="text-red-400 text-sm">{profileError}</p>}
                      <button onClick={handleSaveProfile} disabled={saving} className={btnPrimary}>
                        {saved ? t('settings_saved') : saving ? '...' : t('settings_save')}
                      </button>
                    </div>
                  </div>

                  {/* Password form */}
                  <div className="glass rounded-2xl p-6 border border-white/5">
                    <h3 className="text-white font-medium mb-4">{t('settings_password')}</h3>
                    <div className="space-y-4">
                      <div>
                        <label className={labelClass}>{t('settings_current_pw')}</label>
                        <input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>{t('settings_new_pw')}</label>
                        <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>{t('settings_confirm_pw')}</label>
                        <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} className={inputClass} />
                      </div>
                      {pwError && <p className="text-red-400 text-sm">{pwError}</p>}
                      {pwMessage && <p className="text-emerald-400 text-sm">{pwMessage}</p>}
                      <button onClick={handleChangePassword} disabled={pwSaving || !currentPw || !newPw || !confirmPw} className={btnPrimary}>
                        {pwSaving ? '...' : t('settings_change_pw')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ===== SUBSCRIPTION TAB ===== */}
              {activeTab === 'subscription' && (
                <div className="space-y-6">
                  <div className="glass rounded-2xl p-6 border border-white/5">
                    <h3 className="text-slate-400 text-sm mb-2">{t('settings_plan_current')}</h3>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="px-6 py-3 rounded-2xl bg-gradient-to-r from-emerald-500/20 to-blue-600/20 border border-emerald-500/30">
                        <span className="text-2xl font-bold text-white capitalize">{user.plan}</span>
                      </div>
                    </div>

                    <button onClick={() => navigate('/pricing')} className={btnPrimary}>
                      {t('settings_plan_manage')}
                    </button>

                    {user.plan_updated_at && (
                      <p className="text-slate-500 text-sm mt-4">
                        {t('settings_plan_updated')}: {new Date(user.plan_updated_at).toLocaleDateString()}
                      </p>
                    )}

                    <p className="text-slate-500 text-sm mt-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {t('settings_no_renewal')}
                    </p>
                  </div>
                </div>
              )}

              {/* ===== API KEY TAB ===== */}
              {activeTab === 'api' && (
                <div className="space-y-6">
                  <div className="glass rounded-2xl p-6 border border-white/5">
                    <p className="text-slate-300 text-sm mb-6">{t('settings_api_desc')}</p>

                    {/* No key and no newly generated key */}
                    {!user.api_key_preview && !newApiKey && (
                      <button onClick={handleGenerateApiKey} disabled={apiLoading} className={btnPrimary}>
                        {apiLoading ? '...' : t('settings_api_generate')}
                      </button>
                    )}

                    {/* Newly generated key (show full, once) */}
                    {newApiKey && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                          <p className="text-amber-300 text-xs">{t('settings_api_warning')}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 px-4 py-3 rounded-xl bg-slate-900 border border-white/10 text-emerald-400 text-sm font-mono break-all">
                            {newApiKey}
                          </code>
                          <button onClick={copyApiKey} className="p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                            {apiCopied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-slate-400" />}
                          </button>
                        </div>
                        <button onClick={handleRevokeApiKey} disabled={apiLoading} className="px-4 py-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-colors text-sm">
                          {t('settings_api_revoke')}
                        </button>
                      </div>
                    )}

                    {/* Existing key (preview only) */}
                    {user.api_key_preview && !newApiKey && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <code className="flex-1 px-4 py-3 rounded-xl bg-slate-900 border border-white/10 text-slate-400 text-sm font-mono">
                            {user.api_key_preview}
                          </code>
                        </div>
                        <button onClick={handleRevokeApiKey} disabled={apiLoading} className="px-4 py-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-colors text-sm">
                          {t('settings_api_revoke')}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Usage example */}
                  <div className="glass rounded-2xl p-6 border border-white/5">
                    <h3 className="text-white font-medium mb-3">{t('settings_api_example')}</h3>
                    <pre className="p-4 rounded-xl bg-slate-900 border border-white/10 text-sm text-slate-300 font-mono overflow-x-auto">
{`curl -X POST ${window.location.origin}/api/analyze \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "content_type": "url",
    "content": "https://example.com/article",
    "selected_llms": ["openai"],
    "language": "fr"
  }'`}
                    </pre>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
